import { TempCampaign, WithTemp } from "@/types/campaign";
import { getDefaultCampaign } from "@/utils/common";
import { IDocumentNode, INodeDevice } from "@shared/common/node/type";
import {
  IBannerCampaign,
  IBannerGroupCampaign,
  IEmbeddedInstagramCampaignV2,
  IFormEventCampaign,
  IFormSurveyCampaign,
  IPopupCampaign,
  IPopupGroupCampaign,
  IPromotionCampaign,
} from "@shared/common/types/campaign";
import produce from "immer";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ITempCampaignStore {
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;

  popup: WithTemp<IPopupCampaign> | null;
  popupChild: Record</* group_public_id */ string, WithTemp<IPopupCampaign>>;
  popupGroup: WithTemp<IPopupGroupCampaign> | null;
  banner: Record</* group_public_id */ string, WithTemp<IBannerCampaign>>;
  bannerGroup: WithTemp<IBannerGroupCampaign> | null;
  instagram: WithTemp<IEmbeddedInstagramCampaignV2> | null;
  formEvent: WithTemp<IFormEventCampaign> | null;
  promotion: WithTemp<IPromotionCampaign> | null;
  formSurvey: WithTemp<IFormSurveyCampaign> | null;
  reachedStep: Record<INodeDevice, number>;

  getCampaign(
    campaignType: TempCampaign["type"],
    groupPublicId: string | null
  ): TempCampaign | null;

  resetCampaign(
    campaignType: TempCampaign["type"],
    groupPublicId: string | null,
    language?: string,
    template?: IDocumentNode
  ): TempCampaign;

  dropCampaign(
    campaignType: TempCampaign["type"],
    groupPublicId: string | null
  ): void;

  updateCampaign(
    partialCampaign: Omit<Partial<TempCampaign>, "id">
  ): TempCampaign;

  setReachedStep(device: INodeDevice, step: number): void;
  checkStepRequirement(device: INodeDevice, stepRequirement: number): boolean;
  resetReachedStep(): void;
}

const getDefaultTempCampaign = (
  campaignType: TempCampaign["type"],
  groupPublicId: string | null
) => {
  return {
    ...getDefaultCampaign({
      campaignType,
      parentPublicId: groupPublicId ?? undefined,
    }),
    updatedAt: new Date().toISOString(),
    isTemp: true,
  } as TempCampaign;
};

export const useTempCampaignStore = create(
  persist<ITempCampaignStore>(
    (set, get) => ({
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },

      popup: null,
      popupChild: {},
      popupGroup: null,
      banner: {},
      bannerGroup: null,
      instagram: null,
      formEvent: null,
      promotion: null,
      formSurvey: null,

      reachedStep: { ALL: 0, DESKTOP: 0, MOBILE: 0 },

      getCampaign(campaignType, groupPublicId) {
        switch (campaignType) {
          case "POPUP":
            if (groupPublicId) {
              return get().popupChild[groupPublicId] ?? null;
            }
            return get().popup;
          case "POPUP_GROUP":
            return get().popupGroup;
          case "BANNER":
            if (!groupPublicId) {
              throw new Error("그룹 아이디가 필요합니다.");
            }
            return get().banner[groupPublicId] ?? null;
          case "BANNER_GROUP":
            return get().bannerGroup;
          case "INSTAGRAM_V2":
            return get().instagram;
          case "FORM_EVENT":
            return get().formEvent;
          case "PROMOTION":
            return get().promotion;
          case "FORM_SURVEY":
            return get().formSurvey;
        }
      },

      dropCampaign(campaignType, groupPublicId) {
        switch (campaignType) {
          case "POPUP":
            if (groupPublicId) {
              const map = get().popupChild;
              delete map[groupPublicId];
              set({ popupChild: map });
              break;
            }
            set({ popup: null });
            break;
          case "POPUP_GROUP":
            set({ popupGroup: null });
            break;
          case "BANNER": {
            if (!groupPublicId) {
              throw new Error("그룹 아이디가 필요합니다.");
            }
            const map = get().banner;
            delete map[groupPublicId];
            set({ banner: map });
            break;
          }
          case "BANNER_GROUP":
            set({ bannerGroup: null });
            break;
          case "INSTAGRAM_V2":
            set({ instagram: null });
            break;
          case "FORM_EVENT":
            set({ formEvent: null });
            break;
          case "PROMOTION":
            set({ promotion: null });
            break;
          case "FORM_SURVEY":
            set({ formSurvey: null });
            break;
        }
        return;
      },

      resetCampaign: (campaignType, groupPublicId, language, template) => {
        const tempCampaign = {
          ...getDefaultCampaign({
            campaignType,
            parentPublicId: groupPublicId ?? undefined,
          }),
          language,
          updatedAt: new Date().toISOString(),
          isTemp: true,
        } as TempCampaign;

        if (template) {
          const currentIndex = tempCampaign.data.documents.findIndex(
            (document) => document.device === template.device
          );
          if (currentIndex === -1) {
            tempCampaign.data.documents[0] = template;
          } else {
            tempCampaign.data.documents[currentIndex] = template;
          }
        }

        get().updateCampaign(tempCampaign);

        return tempCampaign;
      },

      updateCampaign(partialCampaign) {
        if (!partialCampaign.type) {
          throw new Error("캠페인 타입이 필요합니다.");
        }

        partialCampaign.updatedAt = new Date().toISOString();

        const currentCampaign =
          get().getCampaign(
            partialCampaign.type,
            partialCampaign.parentPublicId ?? null
          ) ||
          getDefaultTempCampaign(
            partialCampaign.type,
            partialCampaign.parentPublicId ?? null
          );

        const updatedCampaign = produce(currentCampaign, (draft) => {
          Object.assign(draft, partialCampaign);
        });

        switch (updatedCampaign.type) {
          case "POPUP":
            if (updatedCampaign.parentPublicId) {
              set({
                popupChild: {
                  ...get().popupChild,
                  [updatedCampaign.parentPublicId]: updatedCampaign,
                },
              });
              break;
            }
            set({ popup: updatedCampaign });
            break;

          case "POPUP_GROUP":
            set({ popupGroup: updatedCampaign });
            break;

          case "BANNER":
            if (!updatedCampaign.parentPublicId) {
              throw new Error("그룹 아이디가 필요합니다.");
            }
            set({
              banner: {
                ...get().banner,
                [updatedCampaign.parentPublicId]: updatedCampaign,
              },
            });
            break;

          case "BANNER_GROUP":
            set({ bannerGroup: updatedCampaign });
            break;

          case "INSTAGRAM_V2":
            set({ instagram: updatedCampaign });
            break;

          case "FORM_EVENT":
            set({ formEvent: updatedCampaign });
            break;

          case "PROMOTION":
            set({ promotion: updatedCampaign });
            break;

          case "FORM_SURVEY":
            set({ formSurvey: updatedCampaign });
            break;
        }

        return updatedCampaign;
      },

      setReachedStep: (device, step) => {
        if (get().reachedStep[device] >= step) {
          return;
        }
        set({ reachedStep: { ...get().reachedStep, [device]: step } });
      },
      checkStepRequirement: (device, stepRequirement) => {
        return get().reachedStep[device] >= stepRequirement;
      },
      resetReachedStep: () => {
        set({ reachedStep: { ALL: 0, DESKTOP: 0, MOBILE: 0 } });
      },
      onRehydrateStorage: () => {
        set({ _hasHydrated: true });
      },
    }),
    {
      name: "cnb-temp-campaign",
    }
  )
);
